import { useState, useEffect, useRef } from "react";

function useAudioPlayer() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioElementRef = useRef(null);
  const playPromiseRef = useRef(null);

  useEffect(() => {
    const audioElement = audioElementRef.current;
    if (!audioElement) return;

    if (isPlaying) {
      const playPromise = audioElement.play();
      playPromiseRef.current = playPromise;
      if (playPromise && typeof playPromise.catch === "function") {
        // pause()/load() while play() is still pending rejects with AbortError.
        // That interruption is expected, so keep it off the console.
        playPromise.catch((error) => {
          if (error?.name !== "AbortError") {
            console.error("Audio playback failed:", error);
          }
        });
      }
      return;
    }

    // Pausing before a pending play() settles is what triggers the AbortError,
    // so wait for it to resolve first.
    const pendingPlay = playPromiseRef.current;
    if (pendingPlay && typeof pendingPlay.then === "function") {
      pendingPlay.then(() => audioElement.pause()).catch(() => {});
    } else {
      audioElement.pause();
    }
  }, [isPlaying]);

  const isNotPlaying = () => {
    setIsPlaying(false);
  };

  useEffect(() => {
    // Before metadata loads both values are 0, which would otherwise stop
    // playback immediately after it starts.
    if (duration > 0 && currentTime >= duration) {
      setIsPlaying(false);
    }
  }, [currentTime, duration]);

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = (event) => {
    const target = event.currentTarget;
    if (target) {
      if (target.currentTime >= target.duration) {
        setCurrentTime(0);
        target.currentTime = 0;
        isNotPlaying();
      } else {
        setCurrentTime(target.currentTime);
      }
    }
  };

  const handleLoadedMetadata = () => {
    if (
      audioElementRef.current &&
      audioElementRef.current.duration &&
      !Number.isNaN(audioElementRef.current.duration) &&
      audioElementRef.current.duration > 0
    ) {
      setDuration(audioElementRef.current.duration);
    }
  };

  const handleSliderChange = (event) => {
    if (audioElementRef.current) {
      audioElementRef.current.currentTime = event.target.value;
      setCurrentTime(event.target.value);
    }
  };

  const formatTime = (timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  return {
    isPlaying,
    notPlaying: () => setIsPlaying(false),
    currentTime,
    duration,
    audioElementRef,
    handlePlayPause,
    handleTimeUpdate,
    handleLoadedMetadata,
    handleSliderChange,
    formatTime,
    setDuration,
  };
}

export default useAudioPlayer;
